import * as React from "react";
import { Box, Button, ButtonGroup, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { ConnectionNode, Node } from "./tree/tree_";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import useSchematic from "hooks/use_schematic";

import { removeDevice, setDevice } from "pages/elion_site/tabs/devices_tab";

import { Title } from "components/general/typography";
import Loader from "components/general/loader";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CreateIcon from "@mui/icons-material/Create";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  schematicAddNode,
  schematicLinkDevice,
  schematicRemoveNode,
  schematicUnlinkDevice,
  schematicUpload,
} from "scripts/api_v2/schematic_client";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "hooks/use_backend";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LinkIcon from "@mui/icons-material/Link";
import LinkDeviceModal from "components/schematic/link_device_modal";
import { useSnackbar } from "notistack";
import { asKwUnsigned, isBlockedForCommissioning, phasedLimit } from "scripts/utils";
import { CollapsableCard } from "components/general/collapsable_card";

import { DeviceClass, isDisabled, isReadOnly } from "model/device";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import { isComponentDisabledForUpdates } from "pages/login/login_utils";
import SinglePhaseIcon from "components/icons/single_phase";
import ThreePhaseIcon from "components/icons/three_phase_icon";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "enums/react_query_key_enum";
import { fetchSiteData } from "scripts/api_v2/site_client";
import Snackbar from "scripts/snackbar";
import { NodeDialog } from "components/schematic/node/node_dialog";
import { useModal } from "context/modal_provider";
import { useSiteData } from "hooks/use_site";
import ItemStatusIndicator from "../icons/backend_status_icons";
import { TOOLTIPS_EN } from "../../enums/tooltip_enum_EN";
import { AlertColors } from "../alerting/alert_enums";

export function CheckBox({ label, info, checked, setChecked, disabled, ...props }) {
  return (
    <Tooltip placement="top" title={info}>
      <Button
        disabled={disabled}
        onClick={() => setChecked(!checked)}
        sx={{ ...props.sx }}
        size="large"
        endIcon={checked ? <CheckBoxIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
      >
        {label}
      </Button>
    </Tooltip>
  );
}

export default function SchematicCard({ required_privilege }) {
  const params = useParams();
  const backend = useBackend();
  const queryClient = useQueryClient();
  const { openOverrideModal } = useModal();

  const {
    selectedDevice,
    deviceDialogOpen,
    setDeviceDialogOpen,
    linkDeviceOpen,
    setLinkDeviceOpen,
    selectedNode,
    setSelectedNode,
    editing,
    setEditing,
    showPhases,
    setShowPhases,
    isSchematicLoading,
  } = useSchematic();

  let { data: siteData, error, isLoading, refetch } = useSiteData();

  const { enqueueSnackbar } = useSnackbar();

  const permissionMap = useUserPrivilegeKeyPermissionsMap();

  const {
    mutate: schematicLinkDeviceMutation,
    isPending: linkDeviceIsPending,
    isSuccess: linkDeviceIsSuccess,
    isError: linkDeviceIsError,
  } = useMutation({
    mutationFn: async ({ nodeId, deviceId, oldDeviceId }) => {
      if (deviceId === undefined) {
        return await schematicUnlinkDevice(backend.fetch, params.site_id, nodeId, oldDeviceId);
      } else {
        return await schematicLinkDevice(backend.fetch, params.site_id, nodeId, deviceId);
      }
    },
    onSuccess: async (data) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const cp = { ...prev };
        cp.devices_general.schematic = data;
        return cp;
      });
      setLinkDeviceOpen(false);
      setSelectedNode({});
    },
    onError: (err, variables, context) => {
      setLinkDeviceOpen(false);
      setSelectedNode({});
      console.error(err);
      enqueueSnackbar("Un/linking device failed", { variant: "error" });
    },
    onSettled: async () => {
      //await queryClient.invalidateQueries({queryKey:[REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id]})
    },
  });

  const {
    mutate: nodeMutation,
    isPending: nodeIsPending,
    isSuccess: nodeIsSuccess,
    isError: nodeIsError,
  } = useMutation({
    mutationFn: async ({ isRemove, identifier, device_id }) => {
      if (isRemove) {
        return await schematicRemoveNode(backend.fetch, params.site_id, identifier, device_id);
      } else {
        return await schematicAddNode(backend.fetch, params.site_id, identifier, device_id);
      }
    },
    onSuccess: async (data) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const cp = { ...prev };
        cp.devices_general.schematic = data;
        return cp;
      });
    },
    onError: (err, variables, context) => {
      console.error(err);
      enqueueSnackbar("Unlinking device failed", { variant: "error" });
    },
    onSettled: async () => {
      //await queryClient.invalidateQueries({queryKey:[REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id]})
    },
  });

  const {
    mutate: schematicUploadMutation,
    isPending: schematicuploadIsPending,
    isSuccess: schematicuploadIsSuccess,
    isError: schematicuploadIsError,
  } = useMutation({
    mutationFn: async () => {
      return await schematicUpload(backend.fetch, params.site_id);
    },
    onError: (err, variables, context) => {
      console.error(err);
      Snackbar.error("Upload schematic: " + err);
    },
    onSettled: async () => {
      //await queryClient.invalidateQueries({queryKey:[REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id]})
    },
  });

  const handleLinkAction = async (device_id, old_device_id) => {
    schematicLinkDeviceMutation({ nodeId: selectedNode.id, deviceId: device_id, oldDeviceId: old_device_id });
  };

  async function handleQuitEditing() {
    if (editing) {
      schematicUploadMutation();
    }
    setEditing((prev) => !prev);
  }

  const handlePhaseChange = (val) => {
    if (val !== null) {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.SCHEMATIC_METERING, backend.fetch, params.site_id, val],
      });
      setShowPhases(val);
    }
  };

  if (!siteData) {
    return <Loader fill={false} />;
  }

  if (!siteData.devices_general) {
    return (
      <Title ml={5} mt={5}>
        Currently no connected devices
      </Title>
    );
  }

  return (
    <CollapsableCard title="Schematic" defaultOpen={true}>
      {siteData.devices && Object.keys(siteData.devices).length > 0 ? (
        <Schema
          required_privilege={required_privilege}
          isSchematicLoading={isSchematicLoading}
          siteData={siteData}
          nodeMutation={nodeMutation}
        />
      ) : (
        <Title ml={5} mt={5}>
          Currently no connected devices
        </Title>
      )}
      <Box display="flex" justifyContent="right" marginTop={3}>
        <Stack display="flex" flexDirection="row" justifycontent="center" alignItems="center">
          {
            <ItemStatusIndicator
              isMutating={linkDeviceIsPending || nodeIsPending || schematicuploadIsPending}
              success={linkDeviceIsSuccess || nodeIsSuccess || schematicuploadIsSuccess}
              error={linkDeviceIsError || nodeIsError || schematicuploadIsError}
            />
          }
          <ButtonGroup variant="outlined" color="secondary" sx={{ marginLeft: 2 }}>
            <Button
              onClick={() => openOverrideModal({ siteData })}
              disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
              endIcon={<AutorenewIcon fontSize="small" />}
            >
              Manual override
            </Button>
            <CheckBox
              label="Show phases"
              info={"Show L1, L2, L3 if available"}
              checked={showPhases}
              setChecked={handlePhaseChange}
              sx={{ height: 40 }}
            />
            <Tooltip
              title={isBlockedForCommissioning(siteData) ? TOOLTIPS_EN.UPDATES_BLOCKED_DURING_COMMISSIONING : ""}
            >
              <span>
                {" "}
                {/* https://mui.com/material-ui/react-tooltip/#disabled-elements*/}
                <Button
                  disabled={
                    isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey()) ||
                    isBlockedForCommissioning(siteData)
                  }
                  sx={{ height: 40 }}
                  onClick={handleQuitEditing}
                  endIcon={<CreateIcon fontSize="small" />}
                >
                  {editing ? "Quit editing" : "Edit schematic"}
                </Button>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Stack>
      </Box>
      <NodeDialog
        open={deviceDialogOpen}
        schematic={siteData?.devices_general?.schematic}
        node={selectedNode}
        device={selectedDevice}
        setDevice={(dev) => setDevice(dev, queryClient, params.site_id, backend.fetch)}
        removeDevice={(dev) => removeDevice(dev, queryClient, params.site_id, backend.fetch)}
        closeModal={() => {
          setDeviceDialogOpen(false);
        }}
        permissionMap={permissionMap}
        required_privilege={required_privilege}
      />
      <LinkDeviceModal open={linkDeviceOpen} setOpen={setLinkDeviceOpen} handleLinkAction={handleLinkAction} />
    </CollapsableCard>
  );
}

function Schema({ required_privilege, isSchematicLoading, siteData, nodeMutation }) {
  const { editing, setEditing, showPhases, setShowPhases, schematicMetering } = useSchematic();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const queryClient = useQueryClient();
  const backend = useBackend();
  const params = useParams();
  const rootNode = siteData?.devices_general?.schematic;

  /*if (!schematicMetering && !isSchematicLoading) {
        return <Box display="flex" height="100%" alignItems="center" justifyContent="center" mt={5}>
            <Typography>The box is currently offline</Typography>
        </Box>
    }*/

  return (
    <Box display="flex" height="100%" flexDirection="column" alignItems="flex-end" overflow="auto">
      {rootNode ? (
        <Tree
          connectionType={siteData?.config?.connection_type}
          node={rootNode}
          devices={siteData?.devices}
          sx={{ alignSelf: "center" }}
          nodeMutation={nodeMutation}
        />
      ) : (
        <Title ml={5} mt={5}>
          No schematic available for this site
        </Title>
      )}
    </Box>
  );
}

function Tree({ node, devices, connectionType, sx, nodeMutation }) {
  const { nodeMetering, showPhases } = useSchematic();

  if (!node) {
    return null;
  }
  const connection_import = nodeMetering(node.id, "import");

  return (
    <div style={{ paddingLeft: 20, ...sx }}>
      <ConnectionNode
        enlarged={showPhases}
        value={connection_import.value}
        element={<ConnectionNodeElement connectionType={connectionType} />}
      >
        {CreateNode(node, devices, nodeMutation)}
      </ConnectionNode>
    </div>
  );
}

function CreateNode(node, devices, nodeMutation) {
  const device = getNodeDevice(node.device_id, devices);
  const { nodeMetering, showPhases } = useSchematic();
  const node_import = nodeMetering(node.id, "import");
  const curtailment = nodeMetering(node.id, "curtailment");

  const children = node.children.map((child_node) => {
    return CreateNode(child_node, devices, nodeMutation);
  });
  if (device && device.device_class === DeviceClass.BATTERY && node.id !== 0) {
    const soc = nodeMetering(node.id, "soc");
    const flex = nodeMetering(node.id, "flex");
    const other = nodeMetering(node.id, "other");

    const online = device.status && device.status.connection === 1 && device.status.modbus === 1;

    return (
      <Node
        key={device.device_id}
        element={
          <NodeElement
            title={device.name + " (" + Math.round(soc.value) + "%)"}
            device={device}
            node={node}
            value={node_import.value}
            val_l1={node_import.l1}
            val_l2={node_import.l2}
            val_l3={node_import.l3}
            import_constraint={node.import_constraint}
            export_constraint={node.export_constraint}
            online={online}
            nodeMutation={nodeMutation}
          />
        }
        value={node_import.value}
        enlarged={showPhases}
      >
        <Node
          key={device.device_id + "flex"}
          element={
            <NodeElement
              title={"Flex"}
              value={flex.value}
              val_l1={flex.l1}
              val_l2={flex.l2}
              val_l3={flex.l3}
              clickable={false}
              canLink={false}
              online={online}
              nodeMutation={nodeMutation}
            />
          }
          value={flex.value}
          enlarged={showPhases}
        />
        {children.length > 0 ? children : null}
        <Node
          key={device.device_id + "ups"}
          element={
            <>
              <NodeElement
                title={"Other"}
                value={other.value}
                val_l1={other.l1}
                val_l2={other.l2}
                val_l3={other.l3}
                clickable={false}
                curtailed={curtailment.value && curtailment.value != 0}
                canLink={false}
                online={online}
                nodeMutation={nodeMutation}
              />
            </>
          }
          value={other.value}
          enlarged={showPhases}
        />
      </Node>
    );
  }

  if (node.children.length > 0) {
    const um = nodeMetering(node.id, "unmeasured");
    children.push(
      <Node
        key={node.id}
        element={
          <NodeElement
            title="Unmeasured"
            canLink={false}
            clickable={false}
            value={um.value}
            val_l1={um.l1}
            val_l2={um.l2}
            val_l3={um.l3}
            nodeMutation={nodeMutation}
          />
        }
        value={um.value}
        enlarged={showPhases}
      />,
    );
  }

  return (
    <Node
      key={node.id}
      element={
        <NodeElement
          node={node}
          device={device}
          value={node_import.value}
          val_l1={node_import.l1}
          val_l2={node_import.l2}
          val_l3={node_import.l3}
          import_constraint={node.import_constraint}
          export_constraint={node.export_constraint}
          curtailed={curtailment.value && curtailment.value != 0}
          nodeMutation={nodeMutation}
        />
      }
      value={node_import.value}
      enlarged={showPhases}
    >
      {children.length > 0 ? children : null}
    </Node>
  );
}

function NodeElement({
  title,
  value,
  device,
  node,
  import_constraint = null,
  export_constraint = null,
  online,
  canLink = true,
  clickable = true,
  val_l1 = null,
  val_l2 = null,
  val_l3 = null,
  curtailed = false,
  nodeMutation,
}) {
  const {
    editing,
    setSelectedNode,
    setLinkDeviceOpen,
    setSelectedDevice,
    setDeviceDialogOpen,
    showPhases,
    phasesAvailable,
  } = useSchematic();
  const backend = useBackend();
  const params = useParams();
  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const handleClick = () => {
    if (!clickable) return;
    setSelectedNode(node);
    if (editing) {
      setLinkDeviceOpen(true);
    } else {
      setSelectedDevice(device);
      setDeviceDialogOpen(true);
    }
  };

  const limited = () => {
    // DONT SET DOUBLE EQUAL SIGNS OR THE CHECK WILL FAIL
    return import_constraint?.limit != null || export_constraint?.limit != null;
  };

  const connectionType = siteData?.config?.connection_type;

  return (
    <>
      <Tooltip title={!editing && formatConstraints(import_constraint, export_constraint, connectionType)}>
        <Box
          onClick={() => {
            handleClick();
          }}
          width={200}
          minHeight={47}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 3,
            border: (theme) => "1px " + (clickable ? "solid" : "dashed") + " " + theme.palette.secondary.main,
            padding: 0.5,
            opacity: online === false ? 0.5 : 1,
            backgroundColor: (theme) => "white",
            "&:hover": clickable ? { backgroundColor: "#EEE" } : undefined,
            cursor: clickable ? "pointer" : null,
          }}
        >
          <Stack alignItems="center">
            <NodeTitle device={device} title={title} />
            {editing ? (
              canLink ? (
                <LinkDeviceIcon hasDevice={node.device_id} />
              ) : null
            ) : online === false ? (
              "-"
            ) : showPhases && phasesAvailable() ? (
              <PhasedValues limited={limited()} l1={val_l1} l2={val_l2} l3={val_l3} curtailed={curtailed} />
            ) : (
              <NonPhasedValues value={value} limited={limited()} curtailed={curtailed} />
            )}
          </Stack>
        </Box>
      </Tooltip>
      {node && <NodeEditIcon node={node} canRemove={node.id !== 0 && !device} nodeMutation={nodeMutation} />}
    </>
  );
}

function ConnectionNodeElement({ connectionType }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        border: (theme) => "1px solid " + theme.palette.secondary.main,
        backgroundColor: "white",
        minWidth: 40,
        minHeight: 40,
        borderRadius: "50%",
      }}
    >
      {connectionType === 3 ? <ThreePhaseIcon width={18} /> : <SinglePhaseIcon width={18} />}
    </Box>
  );
}

function NodeEditIcon({ node, canAdd = true, canRemove = true, nodeMutation }) {
  const { editing } = useSchematic();

  const style = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    border: (theme) => "1px solid " + theme.palette.background.border,
    backgroundColor: "white",
    "&:hover": { backgroundColor: "#EEE" },
    cursor: "pointer",
  };

  async function handleRemoveNode(node_id, device_id) {
    nodeMutation({ isRemove: true, identifier: node_id, device_id: device_id });
  }

  async function handleAddNode(parent_id, device_id) {
    nodeMutation({ isRemove: false, identifier: parent_id, device_id: device_id });
  }

  if (!editing) {
    return null;
  } else {
    return (
      <Stack direction="column" spacing={2} ml={-2}>
        {canAdd && (
          <Box
            width={33}
            height={33}
            sx={style}
            onClick={() => {
              handleAddNode(node.id, node.device_id);
            }}
          >
            <AddIcon fontSize="small" />
          </Box>
        )}
        {node.children?.length === 0 && canRemove && (
          <Box
            width={33}
            height={33}
            sx={style}
            onClick={() => {
              handleRemoveNode(node.id, node.device_id);
            }}
          >
            <RemoveIcon fontSize="small" />
          </Box>
        )}
      </Stack>
    );
  }
}

function NodeTitle({ title, device }) {
  const theme = useTheme();
  if (!device) {
    return (
      <Typography
        lineHeight="1.2em"
        sx={{ overflow: "hidden", width: "180px", textAlign: "center" }}
        variant="caption"
        noWrap
      >
        <b style={{ color: theme.palette.text.grey }}>{title ? title : "Empty node"}</b>
      </Typography>
    );
  }
  return (
    <Typography
      lineHeight="1.2em"
      sx={{ overflow: "hidden", width: "180px", textAlign: "center" }}
      variant="caption"
      noWrap
    >
      <b style={{ color: theme.palette.text.primary }}>{title ? title : device.name}</b>
      {isDisabled(device)
        ? [<b style={{ color: "lightgrey" }}> • </b>, <b style={{ color: AlertColors.ERROR }}> Disabled </b>]
        : isReadOnly(device)
          ? [<b style={{ color: "lightgrey" }}> • </b>, <b style={{ color: AlertColors.ERROR }}> Read Only </b>]
          : null}
    </Typography>
  );
}

const LinkDeviceIcon = ({ hasDevice = false }) => {
  return <>{hasDevice ? <LinkIcon /> : <AddLinkIcon />}</>;
};

const formatConstraints = (import_constraint, export_constraint, connectionType) => {
  if (!import_constraint?.limit && !export_constraint?.limit) return;

  return (
    <Box margin={1} display="flex" flexDirection="column" justifyContent="center">
      Limits:
      {import_constraint?.limit && (
        <Box display="flex" key={"import"} alignItems="center" justifyContent="center">
          <DirectionArrow right={true} />{" "}
          {asKwUnsigned(phasedLimit(import_constraint.limit, connectionType)) +
            " kW / phase" +
            (import_constraint.safety_margin > 0 ? " (" + parseInt(import_constraint.safety_margin * 100) + "%)" : "")}
          <br />
        </Box>
      )}
      {export_constraint?.limit && (
        <Box display="flex" key={"export"} alignItems="center" justifyContent="center">
          <DirectionArrow right={false} />{" "}
          {asKwUnsigned(phasedLimit(export_constraint.limit, connectionType)) +
            " kW / phase" +
            (export_constraint.safety_margin > 0 ? " (" + parseInt(export_constraint.safety_margin * 100) + "%)" : "")}
          <br />
        </Box>
      )}
    </Box>
  );
};

function DeviceValue({ value, limited, curtailed = false }) {
  return (
    <Stack
      sx={{ color: "#777", fontSize: 14, lineHeight: "1em" }}
      flexDirection="row"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {limited && "["}
      {value !== null && (
        <DirectionArrow
          style={{ fontSize: 14 }}
          zero={asKwUnsigned(value) === "0.0"}
          right={value > 0}
          curtailed={curtailed}
        />
      )}
      {<Typography variant="body">{value !== null && !isNaN(value) ? asKwUnsigned(value) + " kW" : " - "}</Typography>}
      {limited && "]"}
    </Stack>
  );
}

function PhasedValues({ l1, l2, l3, limited, curtailed = false }) {
  return (
    <Stack flexDirection="column" display="flex" justifyContent="center" alignItems="center">
      <DeviceValue value={l1} limited={limited} curtailed={curtailed} />
      <DeviceValue value={l2} limited={limited} curtailed={curtailed} />
      <DeviceValue value={l3} limited={limited} curtailed={curtailed} />
    </Stack>
  );
}

function NonPhasedValues({ value, limited, curtailed = false }) {
  return (
    <Stack flexDirection="column" display="flex" justifyContent="center" alignItems="center">
      <DeviceValue value={value} limited={limited} curtailed={curtailed} />
    </Stack>
  );
}

function DirectionArrow({ zero, right, curtailed = true }) {
  const theme = useTheme();
  const color = !zero ? (right ? AlertColors.ERROR : AlertColors.SUCCESS) : "#c9c8c5";
  const dir =
    curtailed && zero ? "rotate(180deg)" : !zero ? (right ? "rotate(0deg)" : "rotate(180deg)") : "rotate(0deg)";
  return curtailed ? (
    <KeyboardTabIcon style={{ transform: dir, color: color, marginRight: 2, fontSize: 20 }} />
  ) : zero ? (
    <HorizontalRuleIcon style={{ color: color, fontSize: 20 }} />
  ) : (
    <ArrowRightAltIcon style={{ transform: dir, color: color, fontSize: 20 }} />
  );
}

function getNodeDevice(device_id, devices) {
  if (!device_id || device_id === "") {
    return undefined;
  } else {
    return devices[device_id];
  }
}
